/** Очередь заявок в семью. */

import { useState, type ReactNode } from 'react';
import type { FamilyApplication, FamilyApplicationStatus } from '@ak/shared';

import { apiPost } from '../lib/api.ts';
import { formatDateTime } from '../lib/format.ts';
import { linkLabel, splitByLinks } from '../lib/linkify.ts';
import { useResource } from '../lib/useResource.ts';
import { useToast } from '../components/toast.tsx';
import { IconExternal } from '../components/icons.tsx';
import {
  Badge,
  Button,
  EmptyState,
  ErrorState,
  Field,
  Modal,
  PageHeader,
  Panel,
  Skeleton,
  TextArea,
} from '../components/ui.tsx';

const STATUS_LABEL: Record<FamilyApplicationStatus, string> = {
  pending: 'На рассмотрении',
  accepted: 'Принята',
  rejected: 'Отклонена',
};

const STATUS_TONE: Record<FamilyApplicationStatus, 'warning' | 'positive' | 'danger'> = {
  pending: 'warning',
  accepted: 'positive',
  rejected: 'danger',
};

const FILTERS: { value: string; label: string }[] = [
  { value: 'pending', label: 'На рассмотрении' },
  { value: 'accepted', label: 'Принятые' },
  { value: 'rejected', label: 'Отклонённые' },
  { value: '', label: 'Все' },
];

function RejectModal({
  application,
  onClose,
  onDone,
}: {
  application: FamilyApplication;
  onClose: () => void;
  onDone: () => void;
}): ReactNode {
  const toast = useToast();
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);

  /**
   * Два вида отказа различаются только откатом.
   *
   * Отдельными кнопками в самом окне, а не в карточке заявки: в карточке их
   * стало бы четыре в ряд, и при длинном тексте они разъезжались бы. К тому
   * же выбор вида отказа осмысленно делать рядом с причиной, а не до неё.
   */
  const submit = (cooldown: boolean): void => {
    setBusy(true);
    apiPost(`/api/applications/${String(application.id)}/review`, {
      decision: 'rejected',
      comment: comment.trim(),
      cooldown,
    })
      .then(() => {
        toast.success(
          cooldown
            ? 'Заявка отклонена, повторная подача закрыта на 7 дней'
            : 'Заявка отклонена, подать новую можно сразу',
        );
        onDone();
        onClose();
      })
      .catch((cause: unknown) => {
        toast.error(cause instanceof Error ? cause.message : 'Не удалось отклонить заявку');
        setBusy(false);
      });
  };

  return (
    <Modal
      title={`Отклонить заявку #${String(application.id)}`}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Отмена
          </Button>
          <Button
            variant="secondary"
            onClick={() => {
              submit(false);
            }}
            busy={busy}
            title="Человек сможет подать новую заявку сразу"
          >
            Отклонить
          </Button>
          <Button
            variant="danger"
            onClick={() => {
              submit(true);
            }}
            busy={busy}
            title="Новая заявка станет доступна через 7 дней"
          >
            Отклонить на 7 дней
          </Button>
        </>
      }
    >
      <Field
        label="Причина для пользователя"
        hint="Придёт человеку в личные сообщения. Можно оставить пустой"
      >
        <TextArea
          rows={4}
          value={comment}
          autoFocus
          onChange={(event) => {
            setComment(event.target.value);
          }}
        />
      </Field>
    </Modal>
  );
}

export function ApplicationsPage({ timezone }: { timezone: string }): ReactNode {
  const toast = useToast();
  const [filter, setFilter] = useState('pending');
  const [rejecting, setRejecting] = useState<FamilyApplication | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);

  const path = filter === '' ? '/api/applications' : `/api/applications?status=${filter}`;
  const { data, loading, refreshing, error, reload } = useResource<{
    applications: FamilyApplication[];
    names: Record<string, string>;
  }>(path);

  const accept = (application: FamilyApplication): void => {
    setBusyId(application.id);
    apiPost(`/api/applications/${String(application.id)}/review`, { decision: 'accepted' })
      .then(() => {
        toast.success('Заявка принята, пользователю отправлено сообщение');
        reload();
      })
      .catch((cause: unknown) => {
        toast.error(cause instanceof Error ? cause.message : 'Не удалось принять заявку');
      })
      .finally(() => {
        setBusyId(null);
      });
  };

  /**
   * Приглашение на обзвон. Заявка при этом остаётся в работе: обзвон — не
   * решение по ней, а шаг к нему, и решать до разговора нечего.
   */
  const inviteToCall = (application: FamilyApplication): void => {
    setBusyId(application.id);
    apiPost<{ dmStatus: string }>(`/api/applications/${String(application.id)}/invite-call`)
      .then((result) => {
        toast.success(
          result.dmStatus === 'отправлено'
            ? 'Приглашение отправлено, заявка осталась в работе'
            : `Личное сообщение не доставлено: ${result.dmStatus}`,
        );
        reload();
      })
      .catch((cause: unknown) => {
        toast.error(cause instanceof Error ? cause.message : 'Не удалось отправить приглашение');
      })
      .finally(() => {
        setBusyId(null);
      });
  };

  if (error !== null) {
    return <ErrorState message={error} onRetry={reload} />;
  }

  return (
    <>
      <PageHeader
        title="Заявки в семью"
        description="Решение здесь и в Discord — одно и то же: карточка обновится в обоих местах"
      />

      {/* Вкладки вынесены из шапки в свою строку: на телефоне они не влезали
          рядом с заголовком и уезжали за край экрана — «Все» было не нажать.
          Теперь строка занимает всю ширину и при нехватке места прокручивается. */}
      <div
        className="mb-4 -mx-1 flex gap-1 overflow-x-auto rounded-lg p-1"
        style={{ background: 'var(--surface-raised)', scrollbarWidth: 'none' }}
      >
        {FILTERS.map((item) => {
          const active = filter === item.value;

          return (
            <button
              key={item.label}
              type="button"
              onClick={() => {
                setFilter(item.value);
              }}
              className="shrink-0 rounded-md px-3 py-2 text-sm font-medium whitespace-nowrap transition-colors"
              style={active ? { background: 'var(--surface-panel)', color: 'var(--text-primary)' } : { color: 'var(--text-muted)' }}
            >
              {item.label}
              {/* Точка у выбранной вкладки, пока список едет: ответ приходит
                  не мгновенно, и без неё нажатие выглядит как промах. */}
              {active && refreshing && <span className="ml-2 animate-pulse">•</span>}
            </button>
          );
        })}
      </div>

      {loading || data === null ? (
        <Panel>
          <Skeleton rows={6} />
        </Panel>
      ) : data.applications.length === 0 ? (
        <Panel>
          <EmptyState
            title={filter === 'pending' ? 'Нерассмотренных заявок нет' : 'Заявок нет'}
            description="Заявки приходят из канала подачи в Discord."
          />
        </Panel>
      ) : (
        // Пока едет новый список, прежний приглушается: он относится к
        // другому фильтру, и выдавать его за ответ на нажатие нельзя.
        <div
          className="grid gap-3"
          style={{ opacity: refreshing ? 0.5 : 1, transition: 'opacity 120ms ease' }}
        >
          {data.applications.map((application) => (
            <Panel key={application.id} className="p-5">
              {/* Текст растёт до ширины карточки и на узком экране уводит
                  кнопки на свою строку — прижатыми вправо, а не вразнобой.
                  Раньше блок кнопок цеплялся за конец текста и съезжал вслед
                  за ним. */}
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="min-w-0 flex-1 basis-80">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-semibold">{application.gameNick}</span>
                    <Badge tone={STATUS_TONE[application.status]}>{STATUS_LABEL[application.status]}</Badge>
                    <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
                      #{application.id}
                    </span>
                  </div>

                  <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-sm" style={{ color: 'var(--text-secondary)' }}>
                    {/* Идентификатор оставлен в подсказке: по имени человека
                        ищут глазами, а по номеру — когда имя сменилось. */}
                    <span title={application.userId}>
                      Discord: {data.names[application.userId] ?? application.userId}
                    </span>
                    <span>Возраст: {application.age}</span>
                    <span>Адекватность: {application.adequacy}/10</span>
                    <span style={{ color: 'var(--text-muted)' }}>{formatDateTime(application.createdAt, timezone)}</span>
                  </div>

                  {/* Ссылки — плашками, а не голым текстом: в заявках это
                      адреса записей длиной в строку, они разрывали абзац и
                      всё равно не читались. На плашке видно площадку, а
                      полный адрес остаётся в подсказке. */}
                  <p
                    className="mt-3 max-w-3xl text-sm leading-relaxed break-words"
                    style={{ color: 'var(--text-secondary)' }}
                  >
                    {splitByLinks(application.clips).map((part, index) =>
                      part.href === null ? (
                        <span key={index}>{part.text}</span>
                      ) : (
                        <a
                          key={index}
                          href={part.href}
                          title={part.href}
                          target="_blank"
                          // noreferrer вместе с noopener: без первого открытая
                          // страница узнаёт адрес админки, без второго —
                          // получает доступ к вкладке, из которой её открыли.
                          rel="noopener noreferrer"
                          className="mx-0.5 inline-flex max-w-full items-center gap-1.5 rounded-md px-2 py-0.5 align-middle text-[0.8rem] font-medium transition-colors hover:brightness-110"
                          style={{
                            background: 'var(--accent-soft)',
                            color: 'var(--accent)',
                            border: '1px solid var(--border-subtle)',
                          }}
                        >
                          <span className="truncate">{linkLabel(part.href)}</span>
                          <IconExternal size={12} />
                        </a>
                      ),
                    )}
                  </p>

                  {/* Кто позвал на обзвон. Без этой строки второй модератор
                      не видел бы, что человека уже позвали, и звал бы
                      повторно от своего имени — а человек получал бы два
                      разных адресата. */}
                  {application.callInvitedBy !== null && (
                    <p
                      className="mt-3 rounded-lg px-3 py-2 text-sm"
                      style={{ background: 'var(--accent-soft)', color: 'var(--accent)' }}
                    >
                      Приглашение на обзвон отправил{' '}
                      <strong>{data.names[application.callInvitedBy] ?? application.callInvitedBy}</strong>
                      {application.callInvitedAt !== null && ` · ${formatDateTime(application.callInvitedAt, timezone)}`}
                    </p>
                  )}

                  {application.reviewComment !== null && (
                    <p className="mt-3 rounded-lg px-3 py-2 text-sm" style={{ background: 'var(--surface-raised)' }}>
                      Комментарий: {application.reviewComment}
                    </p>
                  )}

                  {/* Кто рассмотрел и до каких пор закрыта повторная подача —
                      ради этого вкладку с принятыми и отклонёнными и
                      открывают. */}
                  {application.status !== 'pending' && (
                    <p className="mt-2 text-xs" style={{ color: 'var(--text-muted)' }}>
                      {application.status === 'accepted' ? 'Принял' : 'Отклонил'}:{' '}
                      {application.reviewerId === null
                        ? 'неизвестно'
                        : (data.names[application.reviewerId] ?? application.reviewerId)}
                      {application.reviewedAt !== null && ` · ${formatDateTime(application.reviewedAt, timezone)}`}
                      {application.status === 'rejected' &&
                        (application.reapplyAt === null
                          ? ' · повторная подача открыта'
                          : ` · повторно с ${formatDateTime(application.reapplyAt, timezone)}`)}
                    </p>
                  )}

                  {application.dmStatus !== null && (
                    <p className="mt-2 text-xs" style={{ color: 'var(--text-muted)' }}>
                      Личное сообщение: {application.dmStatus}
                    </p>
                  )}
                </div>

                {/* На телефоне кнопки идут столбцом во всю ширину: прежде они
                    прижимались вправо и вставали лесенкой — «Отклонить»
                    уезжала под «Принять». */}
                {application.status === 'pending' && (
                  <div className="flex shrink-0 gap-2 max-sm:w-full max-sm:flex-col sm:flex-wrap sm:justify-end">
                    <Button
                      variant="secondary"
                      title="Написать человеку, что заявка рассмотрена и с кем связаться. Заявка останется в работе."
                      onClick={() => {
                        inviteToCall(application);
                      }}
                      disabled={busyId === application.id}
                    >
                      Пригласить на обзвон
                    </Button>
                    <Button
                      variant="primary"
                      onClick={() => {
                        accept(application);
                      }}
                      busy={busyId === application.id}
                    >
                      Принять
                    </Button>
                    <Button
                      variant="danger"
                      onClick={() => {
                        setRejecting(application);
                      }}
                      disabled={busyId === application.id}
                    >
                      Отклонить
                    </Button>
                  </div>
                )}
              </div>
            </Panel>
          ))}
        </div>
      )}

      {rejecting !== null && (
        <RejectModal
          application={rejecting}
          onClose={() => {
            setRejecting(null);
          }}
          onDone={reload}
        />
      )}
    </>
  );
}
